// グリッド表示「一致情報」行と展開帯の状態管理・一致箇所の取得
import { computed, nextTick, ref, watch } from 'vue';

const seekableKinds = ['video', 'audio'];

export const useSearchMatches = ({
  visibleItems,
  activeKeyword,
  selectedItem,
  handleItemClick,
  detailPlayerRef,
  jumpToHit,
}) => {
  const matchesUrlTemplate = ref('');
  const openedMatchVideoId = ref(null);
  const matchesByVideoId = ref({});
  const matchesTruncatedByVideoId = ref({});
  const matchesLoadingByVideoId = ref({});
  const gridColumnCount = ref(1);
  const activeDetailPage = ref(null);

  const isMatchableItem = (item) =>
    !!item && item.item_type !== 'folder';

  const findOpenedItem = () =>
    (visibleItems.value || []).find(
      (item) => isMatchableItem(item) && String(item.id) === String(openedMatchVideoId.value)
    ) || null;

  const matchPanelActiveItem = computed(() =>
    openedMatchVideoId.value === null ? null : findOpenedItem()
  );

  const matchPanelItems = computed(() => {
    if (openedMatchVideoId.value === null) {
      return [];
    }
    const key = String(openedMatchVideoId.value);
    return matchesByVideoId.value[key] || [];
  });

  const matchPanelTruncated = computed(() => {
    if (openedMatchVideoId.value === null) {
      return false;
    }
    const key = String(openedMatchVideoId.value);
    return matchesTruncatedByVideoId.value[key] === true;
  });

  // 開いている帯の分だけ true。先読み中の別ファイルではスピナーを出さない
  const matchPanelLoading = computed(() => {
    if (openedMatchVideoId.value === null) {
      return false;
    }
    const key = String(openedMatchVideoId.value);
    return matchesLoadingByVideoId.value[key] === true;
  });

  const matchPanelInsertIndex = computed(() => {
    if (openedMatchVideoId.value === null) {
      return -1;
    }
    const items = visibleItems.value || [];
    const openedIndex = items.findIndex(
      (item) => isMatchableItem(item) && String(item.id) === String(openedMatchVideoId.value)
    );
    if (openedIndex < 0) {
      return -1;
    }
    const cols = gridColumnCount.value > 0 ? gridColumnCount.value : 1;
    const lastIndex = items.length - 1;
    return Math.min(lastIndex, Math.floor(openedIndex / cols) * cols + cols - 1);
  });

  const updateGridColumnCount = () => {
    const gridEl = document.querySelector('.file-card-grid');
    if (!gridEl) {
      gridColumnCount.value = 1;
      return;
    }
    const raw = window.getComputedStyle(gridEl).gridTemplateColumns;
    const cols = raw ? raw.split(' ').filter(Boolean).length : 0;
    gridColumnCount.value = cols > 0 ? cols : 1;
  };

  const closeMatchPanel = () => {
    openedMatchVideoId.value = null;
  };

  const setMatchesLoading = (key, loading) => {
    const next = { ...matchesLoadingByVideoId.value };
    if (loading) {
      next[key] = true;
    } else {
      delete next[key];
    }
    matchesLoadingByVideoId.value = next;
  };

  const fetchMatches = async (videoId) => {
    const key = String(videoId);
    if (Object.prototype.hasOwnProperty.call(matchesByVideoId.value, key)) {
      return;
    }
    // ホバーで先読みしたものをクリックで二重に投げない
    if (matchesLoadingByVideoId.value[key] === true) {
      return;
    }
    const template = matchesUrlTemplate.value;
    if (!template || !template.includes('__VIDEO_ID__')) {
      return;
    }
    const url = template.replace('__VIDEO_ID__', encodeURIComponent(key));
    // 先読みで応答待ちが常時ある状態になるため、投げたときの検索語を控えておき、
    // 戻ってきた時点で変わっていたら古い一致箇所をキャッシュに入れずに捨てる
    const requestedKeyword = activeKeyword.value || '';
    setMatchesLoading(key, true);
    try {
      const response = await window.axios.get(url, {
        params: { q: requestedKeyword },
      });
      if ((activeKeyword.value || '') !== requestedKeyword) {
        return;
      }
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      const truncated = response?.data?.truncated === true;
      matchesByVideoId.value = { ...matchesByVideoId.value, [key]: items };
      matchesTruncatedByVideoId.value = { ...matchesTruncatedByVideoId.value, [key]: truncated };
    } catch (error) {
      // 失敗時は帯を開かず黙って閉じたままにする
      if (String(openedMatchVideoId.value) === key) {
        closeMatchPanel();
      }
    } finally {
      setMatchesLoading(key, false);
    }
  };

  // 行にカーソルが乗った時点で取りに行く。取得済み・取得中は fetchMatches が即 return する
  const prefetchMatchPanel = (item) => {
    const videoId = item?.id;
    if (videoId === undefined || videoId === null) {
      return;
    }
    void fetchMatches(videoId);
  };

  const toggleMatchPanel = (item) => {
    const videoId = item?.id;
    if (videoId === undefined || videoId === null) {
      return;
    }
    const key = String(videoId);
    if (String(openedMatchVideoId.value) === key) {
      closeMatchPanel();
      return;
    }
    updateGridColumnCount();
    openedMatchVideoId.value = videoId;
    void fetchMatches(videoId);
  };

  const handleResize = () => {
    if (openedMatchVideoId.value !== null) {
      updateGridColumnCount();
    }
  };

  const initSearchMatches = (dataSource) => {
    matchesUrlTemplate.value =
      dataSource?.getAttribute('data-video-matches-url-template') || '';
    window.addEventListener('resize', handleResize);
  };

  const teardownSearchMatches = () => {
    window.removeEventListener('resize', handleResize);
  };

  watch(activeKeyword, () => {
    // 検索語が変わったらキャッシュを捨てて帯を閉じる
    matchesByVideoId.value = {};
    matchesTruncatedByVideoId.value = {};
    matchesLoadingByVideoId.value = {};
    closeMatchPanel();
  });

  watch(visibleItems, () => {
    if (openedMatchVideoId.value === null) {
      return;
    }
    if (!findOpenedItem()) {
      closeMatchPanel();
    }
  });

  // 選択ファイルが切り替わったら、保持しているページ番号を捨てる。
  // handleMatchLabelClick 内で handleItemClick した直後に activeDetailPage を
  // 設定し直すため、この watch は同じ更新サイクル内で先に走らせる必要がある
  // (flush: 'sync' でないと、後段の代入が watch の reset で上書きされる)。
  watch(
    selectedItem,
    (next, prev) => {
      const nextId = next ? String(next.id) : null;
      const prevId = prev ? String(prev.id) : null;
      if (nextId !== prevId) {
        activeDetailPage.value = null;
      }
    },
    { flush: 'sync' }
  );

  const applyPageNumber = (hit) => {
    const page = Number(hit?.page_no);
    if (!Number.isInteger(page) || page < 1) {
      return;
    }
    if (activeDetailPage.value === page) {
      // 同じページ番号を再度指定した場合、値が変わらないと
      // PdfPreviewPdfjs.vue 側の initialPage watch が発火しない
      // (Vue の ref は同値代入を無視する) ため、一旦 null に落としてから
      // 同一 tick 内で元のページに戻し、watch を強制的に再発火させる。
      activeDetailPage.value = null;
      nextTick(() => {
        activeDetailPage.value = page;
      });
      return;
    }
    activeDetailPage.value = page;
  };

  const seekToHit = (item, hit) => {
    if (hit?.start_sec === null || hit?.start_sec === undefined) {
      return;
    }
    const start = Number(hit.start_sec);
    if (
      seekableKinds.includes(item.kind) &&
      item.can_preview &&
      item.manifest_url &&
      Number.isFinite(start) &&
      start >= 0
    ) {
      detailPlayerRef.value?.seekTo(start);
    }
  };

  const handleMatchLabelClick = (hit) => {
    const item = matchPanelActiveItem.value;
    if (!item) {
      return;
    }
    const current = selectedItem.value;
    const alreadySelected = current && String(current.id) === String(item.id);

    if (alreadySelected) {
      applyPageNumber(hit);
      seekToHit(item, hit);
      void jumpToHit?.(hit);
      return;
    }

    // サムネイルクリックと同じ選択処理を再利用する。videojs は
    // :key="`player-${selectedItem.id}`" で選択切り替え時に再マウントされる
    // ため、ref の張り替えが終わる次の DOM 更新後までシークを遅らせる。
    handleItemClick(item, null, null);
    applyPageNumber(hit);
    nextTick(() => {
      seekToHit(item, hit);
      void jumpToHit?.(hit);
    });
  };

  return {
    openedMatchVideoId,
    matchPanelActiveItem,
    matchPanelItems,
    matchPanelTruncated,
    matchPanelLoading,
    matchPanelInsertIndex,
    activeDetailPage,
    toggleMatchPanel,
    prefetchMatchPanel,
    closeMatchPanel,
    handleMatchLabelClick,
    applyPageNumber,
    initSearchMatches,
    teardownSearchMatches,
  };
};
